import { KNOWN_DIRECTIVE_NAMES } from './remarkAiDirectives';

const NAMES = KNOWN_DIRECTIVE_NAMES.join('|');

// A code span whose entire content is a single known directive — the model
// "code-formatting" a reference it was told to emit bare:  `:documentation{id=x}`
//
// Deliberately narrow:
// - `[ \t]*`, never `\s*`: a fenced code block is the same backtick run with the
//   directive on its own line between them (```\n:documentation{id=x}\n```), so
//   allowing newlines here would match the FENCE and unwrap it — exactly the case
//   the design promises to leave literal.
// - the content may not contain a backtick or newline, so the match can only ever
//   be one single-line code span.
// - the span must hold the directive and NOTHING else: a code span that mixes
//   prose with a directive (`see :documentation{id=x} now`) is a genuine code
//   sample, not a mis-formatted reference.
// - the opening and closing runs must be the same length AND not butt up against
//   another backtick (the `(^|[^`])` prefix and the trailing lookahead): a run
//   that finds no equal-length partner (``x`) opens no code span at all, so its
//   backticks are already literal text and must be left alone. Matched via a
//   prefix group rather than a lookbehind for browser support.
const BACKTICKED_DIRECTIVE_RE = new RegExp(
  `(^|[^\`])(\`+)[ \\t]*(:{1,2}(?:${NAMES})\\{[^}\`\\n]*\\})[ \\t]*\\2(?!\`)`,
  'g',
);

// The attribute group must actually carry an id — `{id=x}` or the `{#x}`
// shorthand — matching what remarkAiDirectives can render. `version-id=v9` alone
// must NOT qualify, hence the leading space/brace boundary before `id=`.
const HAS_ID_RE = /\{(?:[^}]*[ \t])?(?:id=|#)/;

// Un-code-format a directive the model wrapped in backticks.
//
// The BE is prompted to emit `:documentation{id=…}` bare, but the model often
// treats the token as code and writes it as `` `:documentation{id=…}` ``. Markdown
// code spans are literal by definition, so remark-directive never sees a directive
// there and the reader gets raw `:documentation{id=…}` text in code style instead
// of a document chip.
//
// This runs on the source, BEFORE the parse: remark-directive is a micromark
// syntax extension, so by the time any remark transform sees the tree the
// backticks have already become an `inlineCode` node and the directive is gone.
// Stripping only the backticks keeps remark-directive the single owner of the
// directive grammar — attributes, quoting and the `{#id}` shorthand are still
// parsed by it, not re-implemented here.
//
// A directive inside a FENCED code block stays literal, as designed: fences are
// excluded by the newline rule above, and a bare (un-backticked) directive in a
// fence is never touched. The one accepted cosmetic edge: a backticked directive
// written INSIDE a fence loses its backticks in the displayed sample — it still
// renders as literal code, never as a chip.
export default function unwrapBacktickedDirectives(text: string): string {
  return text.replace(
    BACKTICKED_DIRECTIVE_RE,
    (match, prefix, _ticks, directive) =>
      HAS_ID_RE.test(directive) ? `${prefix}${directive}` : match,
  );
}
